import React from 'react';
import { ARCHETYPES } from '../../config/archetypes';

// ─── Score dial ───────────────────────────────────────────────────────────────

function getScoreColor(score, max) {
  const pct = score / max;
  if (pct >= 0.75) return { ring: '#3fb950', text: '#3fb950', bg: 'rgba(63,185,80,0.10)' };
  if (pct >= 0.5)  return { ring: '#d29922', text: '#d29922', bg: 'rgba(210,153,34,0.10)' };
  return { ring: '#f85149', text: '#f85149', bg: 'rgba(248,81,73,0.10)' };
}

function ScoreDial({ score, max }) {
  const c = getScoreColor(score, max);
  return (
    <div
      className="flex items-center justify-center w-16 h-16 rounded-full border-4 flex-shrink-0"
      style={{ borderColor: c.ring, backgroundColor: c.bg }}
    >
      <div className="text-center">
        <span className="text-xl font-bold" style={{ color: c.text }}>{score}</span>
        <span className="text-[#8b949e] text-[9px] block">/{max}</span>
      </div>
    </div>
  );
}

// ─── Level badge for leveled archetypes ───────────────────────────────────────

const LEVEL_LABELS = {
  scaffold_9:  ['L1', 'L2', 'L3'],
  essay_20:    ['L1', 'L2', 'L3', 'L4'],
  envsci_9:    ['L1', 'L2', 'L3'],
  envsci_25:   ['L1', 'L2', 'L3', 'L4', 'L5'],
};

const LEVEL_COLORS = {
  1: { text: '#f85149', bg: 'rgba(248,81,73,0.10)', border: 'rgba(248,81,73,0.30)' },
  2: { text: '#d29922', bg: 'rgba(210,153,34,0.10)', border: 'rgba(210,153,34,0.30)' },
  3: { text: '#58a6ff', bg: 'rgba(88,166,255,0.10)', border: 'rgba(88,166,255,0.30)' },
  4: { text: '#3fb950', bg: 'rgba(63,185,80,0.10)', border: 'rgba(63,185,80,0.30)' },
};

function LevelBadge({ level, archetype }) {
  if (!level) return null;
  const labels = LEVEL_LABELS[archetype] ?? [];
  const label = labels[level - 1] ?? `L${level}`;
  const c = LEVEL_COLORS[level] ?? LEVEL_COLORS[1];
  return (
    <span
      className="text-[10px] font-mono font-bold px-2 py-0.5 rounded border"
      style={{ color: c.text, backgroundColor: c.bg, borderColor: c.border }}
    >
      {label}
    </span>
  );
}

// ─── Card feedback (outline_4 / assess_6 / data_6) ───────────────────────────

function CardBreakdown({ cardFeedback, cardLabels }) {
  return (
    <div className="space-y-2">
      {cardLabels.map((lbl) => {
        const cf = cardFeedback[lbl] ?? {};
        const awarded = cf.marks ?? 0;
        return (
          <div key={lbl} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono text-slate-400 font-semibold">{lbl}</span>
              <span
                className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                  awarded === 1
                    ? 'text-[#3fb950] bg-[#3fb950]/10'
                    : 'text-[#f85149] bg-[#f85149]/10'
                }`}
              >
                [{awarded}/1]
              </span>
            </div>
            {cf.comment && (
              <p className="text-[#8b949e] text-xs leading-relaxed">{cf.comment}</p>
            )}
            {cf.minimal_improvement && (
              <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">
                ✏ {cf.minimal_improvement}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Paragraph feedback (scaffold_9 / essay_20) ───────────────────────────────

function ParagraphBreakdown({ paragraphFeedback }) {
  if (!Array.isArray(paragraphFeedback) || paragraphFeedback.length === 0) return null;
  return (
    <div className="space-y-2">
      {paragraphFeedback.map((para, i) => {
        const label = para.card ?? para.paragraph ?? `Paragraph ${i + 1}`;
        return (
          <div key={i} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono text-slate-400 font-semibold">{label}</span>
              {(para.ao1 !== undefined || para.ao2 !== undefined) && (
                <span className="text-[9px] font-mono text-[#484f58]">
                  AO1: {para.ao1 ?? '—'} · AO2: {para.ao2 ?? '—'}
                </span>
              )}
            </div>
            {para.comment && (
              <p className="text-[#8b949e] text-xs leading-relaxed">{para.comment}</p>
            )}
            {para.improvement && (
              <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">
                ✏ {para.improvement}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Single question result card ──────────────────────────────────────────────

function QuestionResult({ index, question, result, answer }) {
  const feedback = result?.markingResult ?? null;
  const archetype = question?.archetype || 'outline_4';
  const archetypeConfig = ARCHETYPES[archetype] ?? ARCHETYPES.outline_4;
  const maxMarks = question?.marks ?? archetypeConfig.marks ?? 4;
  const cardLabels = archetypeConfig.cards.map((c) => c.label);

  const score = feedback?.total ?? feedback?.awarded_mark ?? 0;
  const level = feedback?.level ?? feedback?.awarded_level ?? null;
  const isLeveled = archetype === 'scaffold_9' || archetype === 'essay_20'
    || archetype === 'envsci_9' || archetype === 'envsci_25';
  const cardFeedback = feedback?.card_feedback ?? {};
  const paragraphFeedback = feedback?.paragraph_feedback ?? null;

  return (
    <div className="rounded-xl border border-[#30363d] bg-[#0d1117] overflow-hidden">
      {/* Question header */}
      <div className="px-4 py-3 bg-[#161b22] border-b border-[#30363d]">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <div className="text-[10px] font-mono text-[#8b949e]">
                Q{index + 1} · {question?.year}{question?.paper != null ? ` Paper ${question.paper}` : question?.source_paper ? ` ${question.source_paper}` : ''} · {question?.question_number}
              </div>
              <span
                className="text-[9px] font-mono px-1.5 py-0.5 rounded border"
                style={{
                  color: archetypeConfig.hex,
                  borderColor: `${archetypeConfig.hex}40`,
                  backgroundColor: `${archetypeConfig.hex}10`,
                }}
              >
                {archetypeConfig.label} · {maxMarks}m
              </span>
            </div>
            <p className="text-[#e6edf3] text-xs leading-relaxed font-medium line-clamp-2">
              {question?.question_text}
            </p>
          </div>
          {result?.markingStatus === 'completed' && feedback ? (
            <ScoreDial score={score} max={maxMarks} />
          ) : result?.markingStatus === 'error' ? (
            <div className="w-16 h-16 rounded-full border-4 border-[#f85149]/40 bg-[#f85149]/5 flex items-center justify-center flex-shrink-0">
              <span className="text-[#f85149] text-xs font-mono">err</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Feedback body */}
      {result?.markingStatus === 'completed' && feedback ? (
        <div className="px-4 py-4 space-y-4">
          {/* Level badge for leveled archetypes */}
          {isLeveled && level && (
            <div className="flex items-center gap-2">
              <LevelBadge level={level} archetype={archetype} />
              <span className="text-[10px] font-mono text-[#484f58]">awarded level</span>
            </div>
          )}

          {/* Student answer condensed */}
          {answer?.steps && (
            <div>
              <p className="text-[#8b949e] text-[10px] font-mono font-semibold uppercase tracking-wider mb-1">
                Your Answer
              </p>
              <p className="text-[#e6edf3] text-xs leading-relaxed">
                {cardLabels.map((lbl) => answer.steps[lbl]?.trim()).filter(Boolean).join(' ')}
              </p>
            </div>
          )}

          {/* Justification */}
          {feedback.justification && (
            <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
              <div className="text-[10px] font-mono leading-relaxed">
                <span className="text-[#8b949e]">justification</span>
                <span className="text-[#484f58]"> » </span>
                <span className="text-[#e6edf3]">{feedback.justification}</span>
              </div>
            </div>
          )}

          {/* Card breakdown for point/mark-per-card archetypes */}
          {!isLeveled && Object.keys(cardFeedback).length > 0 && (
            <div>
              <p className="text-[#8b949e] text-[10px] font-mono font-semibold uppercase tracking-wider mb-2">
                Card Breakdown
              </p>
              <CardBreakdown cardFeedback={cardFeedback} cardLabels={cardLabels} />
            </div>
          )}

          {/* Paragraph breakdown for leveled essay archetypes */}
          {isLeveled && paragraphFeedback && (
            <div>
              <p className="text-[#8b949e] text-[10px] font-mono font-semibold uppercase tracking-wider mb-2">
                Paragraph Feedback
              </p>
              <ParagraphBreakdown paragraphFeedback={paragraphFeedback} />
            </div>
          )}

          {/* Examiner note */}
          {feedback.examiner_note && (
            <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
              <div className="text-[10px] font-mono leading-relaxed">
                <span className="text-[#8b949e]">examiner_note</span>
                <span className="text-[#484f58]"> » </span>
                <span className="text-[#e6edf3]">{feedback.examiner_note}</span>
              </div>
            </div>
          )}
        </div>
      ) : result?.markingStatus === 'error' ? (
        <div className="px-4 py-4">
          <p className="text-[#f85149] text-xs font-mono">
            Marking failed for this question. It has been scored 0.
          </p>
        </div>
      ) : null}
    </div>
  );
}

// ─── Spinner placeholder for unrevealed questions ─────────────────────────────

function QuestionPending({ index, isActive }) {
  return (
    <div className="rounded-xl border border-[#30363d] bg-[#161b22] px-4 py-4">
      <div className="flex items-center gap-3">
        {isActive ? (
          <span
            className="w-4 h-4 rounded-full border-2 border-[#8b949e] border-t-[#58a6ff] animate-spin flex-shrink-0"
            style={{ animationDuration: '800ms' }}
          />
        ) : (
          <span className="w-4 h-4 rounded-full border-2 border-[#484f58] flex-shrink-0" />
        )}
        <div className="flex-1">
          <div
            className="h-2 rounded bg-[#30363d] animate-pulse"
            style={{ width: `${50 + (index * 13) % 30}%` }}
          />
        </div>
        <span className="text-[10px] font-mono text-[#484f58]">Q{index + 1}</span>
      </div>
    </div>
  );
}

// ─── Main Reveal component ────────────────────────────────────────────────────

export default function MockExamReveal({ questions, results, answers, revealedUpTo, onExit }) {
  const allRevealed = revealedUpTo >= questions.length - 1;

  const totalScore = results
    .filter((r) => r.markingStatus === 'completed' && r.markingResult)
    .reduce((sum, r) => {
      const fb = r.markingResult;
      return sum + (fb?.total ?? fb?.awarded_mark ?? 0);
    }, 0);

  const maxScore = questions.reduce((sum, q) => {
    const config = ARCHETYPES[q.archetype] ?? ARCHETYPES.outline_4;
    return sum + (q.marks ?? config.marks ?? 4);
  }, 0);

  const c = getScoreColor(totalScore, maxScore);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-b border-[#30363d] bg-[#0d1117]">
        <span className="text-xs font-mono text-[#8b949e]">Results</span>
        <div className="flex items-center gap-3">
          {allRevealed && (
            <div
              className="flex items-center gap-2 px-3 py-1 rounded-full border-2"
              style={{ borderColor: c.ring, backgroundColor: c.bg }}
            >
              <span className="text-sm font-bold" style={{ color: c.text }}>
                {totalScore}/{maxScore}
              </span>
              <span className="text-[10px] font-mono" style={{ color: c.text }}>
                total
              </span>
            </div>
          )}
          <button
            onClick={onExit}
            className="w-6 h-6 flex items-center justify-center rounded-full text-[#484f58] hover:text-[#e6edf3] hover:bg-[#30363d] transition-colors text-xs"
            aria-label="Close results"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Question cards */}
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        {questions.map((q, i) => {
          const result = results.find((r) => r.questionId === q.id) ?? null;
          if (i <= revealedUpTo) {
            return (
              <QuestionResult
                key={q.id}
                index={i}
                question={q}
                result={result}
                answer={answers[q.id]}
              />
            );
          }
          return (
            <QuestionPending
              key={q.id}
              index={i}
              isActive={i === revealedUpTo + 1}
            />
          );
        })}

        {/* Final summary */}
        {allRevealed && (
          <div
            className="rounded-xl border-2 px-5 py-5 text-center"
            style={{ borderColor: c.ring, backgroundColor: c.bg }}
          >
            <div className="text-[10px] font-mono text-[#8b949e] uppercase tracking-wider mb-1">
              Final Score
            </div>
            <div className="text-4xl font-bold mb-1" style={{ color: c.text }}>
              {totalScore}/{maxScore}
            </div>
            <div className="text-xs text-[#8b949e]">
              {Math.round((totalScore / maxScore) * 100)}% · {questions.length} questions marked
            </div>
            <button
              onClick={onExit}
              className="mt-4 px-6 py-2 rounded-lg border border-[#30363d] text-[#8b949e] hover:text-[#e6edf3] hover:border-[#58a6ff]/40 text-xs font-mono transition-colors"
            >
              Exit Exam
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
